import { CLOSING_TIMES } from '@/components/explore/constants/filters'
import { countActiveFilters } from '@/components/explore/utils'
import { SORT_KEYS } from '@/lib/api/params'
import type {
	FilterConfig,
	ListType,
	SortConfig,
	SortKey,
	ViewMode,
} from '@/lib/merge-stream/types'
import { LIST_TYPES, VIEW_MODES } from '@/lib/merge-stream/types'
import { ALL_CATEGORIES, ALL_PLATFORMS, DEFAULT_PLATFORMS } from '@/types/market'
import type { Category, Platform } from '@/types/market'
import {
	parseAsArrayOf,
	parseAsBoolean,
	parseAsInteger,
	parseAsString,
	parseAsStringLiteral,
	useQueryStates,
} from 'nuqs'
import { useCallback, useMemo, useState } from 'react'

const exploreFiltersParsers = {
	sort: parseAsStringLiteral(SORT_KEYS).withDefault('volume'),
	view: parseAsStringLiteral(VIEW_MODES).withDefault('tiles'),
	list: parseAsStringLiteral(LIST_TYPES).withDefault('events'),
	categories: parseAsArrayOf(parseAsStringLiteral(ALL_CATEGORIES)).withDefault([]),
	expiring: parseAsBoolean.withDefault(false),
	platforms: parseAsArrayOf(parseAsStringLiteral(ALL_PLATFORMS)).withDefault(DEFAULT_PLATFORMS),
	activeOnly: parseAsBoolean.withDefault(true),
	tradableOnly: parseAsBoolean.withDefault(false),
	minVol: parseAsInteger,
	maxVol: parseAsInteger,
	minLiq: parseAsInteger,
	maxLiq: parseAsInteger,
	closing: parseAsStringLiteral(CLOSING_TIMES),
	minProb: parseAsInteger,
	q: parseAsString.withDefault(''),
}

const NUQS_OPTIONS = { history: 'replace' as const, shallow: true }

export function useExploreFilters() {
	const [state, setState] = useQueryStates(exploreFiltersParsers, NUQS_OPTIONS)

	// --- Derived values ---

	const sort: SortConfig = useMemo(
		() => ({ key: state.sort, direction: 'desc' as const }),
		[state.sort]
	)

	const viewMode: ViewMode = state.view
	const listType: ListType = state.list
	const selectedCategories = state.categories as Category[]
	const expiringSelected: boolean = state.expiring
	const [watchlistActive, setWatchlistActive] = useState(false)
	const enabledPlatforms = state.platforms as Platform[]

	const filters: FilterConfig = useMemo(
		() => ({
			activeOnly: state.activeOnly,
			tradableOnly: state.tradableOnly,
			minVolume: state.minVol ?? undefined,
			maxVolume: state.maxVol ?? undefined,
			minLiquidity: state.minLiq ?? undefined,
			maxLiquidity: state.maxLiq ?? undefined,
			closingTime: state.closing ?? undefined,
			minProbability: state.minProb ?? undefined,
		}),
		[
			state.activeOnly,
			state.tradableOnly,
			state.minVol,
			state.maxVol,
			state.minLiq,
			state.maxLiq,
			state.closing,
			state.minProb,
		]
	)

	const searchQuery = state.q

	const effectiveFilters: FilterConfig = useMemo(
		() => ({
			...filters,
			categories: selectedCategories.length > 0 ? selectedCategories : undefined,
			search: searchQuery || undefined,
			expiringSoon: expiringSelected || undefined,
		}),
		[filters, selectedCategories, searchQuery, expiringSelected]
	)

	const activeFilterCount = useMemo(() => countActiveFilters(effectiveFilters), [effectiveFilters])

	const hasActiveFilters =
		activeFilterCount > 0 || selectedCategories.length > 0 || !!searchQuery || expiringSelected

	// --- Setters / Handlers ---

	const handleSortChange = useCallback(
		(sortKey: SortKey) => {
			setState({ sort: sortKey })
		},
		[setState]
	)

	const handleViewModeChange = useCallback(
		(mode: ViewMode) => {
			setState({ view: mode })
			if (mode === 'list') setWatchlistActive(false)
		},
		[setState]
	)

	const handleListTypeChange = useCallback(
		(type: ListType) => {
			setState({ list: type })
		},
		[setState]
	)

	const handleToggleCategory = useCallback(
		(category: Category) => {
			setState(prev => {
				const current = prev.categories
				const updated = current.includes(category)
					? current.filter(c => c !== category)
					: [...current, category]
				return { categories: updated }
			})
		},
		[setState]
	)

	const handleSelectAllCategories = useCallback(() => {
		setState({ categories: [], expiring: false })
	}, [setState])

	const handleToggleExpiring = useCallback(() => {
		setState(prev => ({ expiring: !prev.expiring }))
	}, [setState])

	const handleToggleWatchlist = useCallback(() => {
		setWatchlistActive(prev => {
			const next = !prev
			if (next && state.view !== 'tiles') {
				setState({ view: 'tiles' as ViewMode })
			}
			return next
		})
	}, [setState, state.view])

	const togglePlatform = useCallback(
		(platform: Platform) => {
			setState(prev => {
				const current = prev.platforms
				const updated = current.includes(platform)
					? current.filter(p => p !== platform)
					: [...current, platform]
				return { platforms: updated }
			})
		},
		[setState]
	)

	const setFilters = useCallback(
		(newFilters: FilterConfig) => {
			setState({
				activeOnly: newFilters.activeOnly ?? true,
				tradableOnly: newFilters.tradableOnly ?? false,
				minVol: newFilters.minVolume ?? null,
				maxVol: newFilters.maxVolume ?? null,
				minLiq: newFilters.minLiquidity ?? null,
				maxLiq: newFilters.maxLiquidity ?? null,
				closing: (newFilters.closingTime as (typeof CLOSING_TIMES)[number]) ?? null,
				minProb: newFilters.minProbability ?? null,
			})
		},
		[setState]
	)

	const setViewMode = useCallback(
		(mode: ViewMode) => {
			setState({ view: mode })
		},
		[setState]
	)

	const setListType = useCallback(
		(type: ListType) => {
			setState({ list: type })
		},
		[setState]
	)

	const setSort = useCallback(
		(sortConfig: SortConfig) => {
			setState({ sort: sortConfig.key })
		},
		[setState]
	)

	const setSelectedCategories = useCallback(
		(categories: Category[]) => {
			setState({ categories })
		},
		[setState]
	)

	const setSearchQuery = useCallback(
		(q: string) => {
			setState({ q: q || null })
		},
		[setState]
	)

	return {
		// State
		sort,
		filters,
		viewMode,
		listType,
		selectedCategories,
		expiringSelected,
		watchlistActive,
		enabledPlatforms,
		effectiveFilters,
		activeFilterCount,
		hasActiveFilters,
		searchQuery,

		// Handlers (for ExploreFiltersBar)
		handleSortChange,
		handleViewModeChange,
		handleListTypeChange,
		handleToggleCategory,
		handleSelectAllCategories,
		handleToggleExpiring,
		handleToggleWatchlist,
		togglePlatform,

		// Setters (for FilterModal and direct use)
		setFilters,
		setSort,
		setViewMode,
		setListType,
		setSelectedCategories,
		setSearchQuery,
	}
}
